import { HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class PhotoCategory extends BaseCategory {
    get name() {
        return "Photo";
    }

    get type() {
        return SlideCategories.PHOTO;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for one or more photos with no text.";
    }

    getPrompt() {
        return `List the key subjects or concepts that should be depicted in the photo(s) for this slide. Focus on the main elements that convey the intended message or theme of the slide.`;
    }

    getImageDescription() {
        return `Describe the subject and contents of the image. Do not include instructions for style, color, or composition unless specifically relevant to the content.
Make sure the prompt is clear and descriptive but not long or overly detailed (ie. "close up of the moon", "two business people in a meeting", "abstract representation of time").
${this.getProperNounImageDescriptionInstruction()}`;
    }

    getPromptSchema(options) {
        return {
            photos: [{
                properNouns: [this.getImageProperNounsInstruction("the image description and slide content")],
                imageDescription: this.getImageDescription()
            }]
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.photos)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [];

        models.push(this.buildPhotoCollageSlide(model, options));

        return models;
    }

    buildPhotoCollageSlide(model, options = {}) {
        let builder = new SlideModelBuilder("collage", model);

        // Only add header if there's a title
        if (model.slideTitle && model.userPromptAnalysis?.hasTitle) {
            builder.createHeader({
                heading: model.slideTitle,
                position: HeaderPositionType.TOP
            });
        } else {
            // No header for photo-only slides
            builder.model.layout.headerPosition = HeaderPositionType.NONE;
        }

        builder.createPrimary({
            gridLayout: this.getLayoutsForImageCount(model.photos.length),
            layoutType: "grid",
            showGutter: true,
            fullBleed: true,
            items: model.photos.map((photo, index) => ({
                componentType: "TextAndImage",
                childElement: {
                    content_type: "image",
                    enableFaceDetection: true,
                    ...builder.createImage(this.ensureSearchIncludesProperNouns(photo.imageDescription, photo.properNouns, [model.slideTitle, photo.imageDescription]), {}, false, {
                        description: photo.imageDescription,
                        slideContent: JSON.stringify([model.slideTitle, photo.imageDescription]),
                        properNouns: photo.properNouns,
                        subjectType: "photo"
                    })
                }
            }))
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    getLayoutsForImageCount(count) {
        const layouts = {
            1: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 12 }] }, fullBleed: false, showGutter: false, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 12 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            2: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 6, height: 12 }, { x: 6, y: 0, width: 6, height: 12 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 6 }, { x: 0, y: 6, width: 12, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 8, height: 12 }, { x: 8, y: 0, width: 4, height: 12 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            3: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 6, height: 12 }, { x: 6, y: 0, width: 6, height: 6 }, { x: 6, y: 6, width: 6, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 12 }, { x: 4, y: 0, width: 4, height: 12 }, { x: 8, y: 0, width: 4, height: 12 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 6 }, { x: 0, y: 6, width: 6, height: 6 }, { x: 6, y: 6, width: 6, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            4: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 6, height: 6 }, { x: 6, y: 0, width: 6, height: 6 }, { x: 0, y: 6, width: 6, height: 6 }, { x: 6, y: 6, width: 6, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 8, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 8, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            5: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 12 }, { x: 4, y: 0, width: 4, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 8, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            6: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 6 }, { x: 4, y: 0, width: 4, height: 6 }, { x: 8, y: 0, width: 4, height: 6 }, { x: 0, y: 6, width: 4, height: 6 }, { x: 4, y: 6, width: 4, height: 6 }, { x: 8, y: 6, width: 4, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 8 }, { x: 0, y: 8, width: 3, height: 4 }, { x: 3, y: 8, width: 2, height: 4 }, { x: 5, y: 8, width: 2, height: 4 }, { x: 7, y: 8, width: 2, height: 4 }, { x: 9, y: 8, width: 3, height: 4 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" },
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 9, height: 8 }, { x: 9, y: 0, width: 3, height: 4 }, { x: 0, y: 8, width: 3, height: 4 }, { x: 3, y: 8, width: 3, height: 4 }, { x: 6, y: 8, width: 3, height: 4 }, { x: 9, y: 4, width: 3, height: 8 }] }, fullBleed: true, showGutter: false, layoutEngineType: "flex" }
            ],
            7: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 3, height: 12 }, { x: 3, y: 0, width: 3, height: 6 }, { x: 6, y: 0, width: 3, height: 6 }, { x: 3, y: 6, width: 3, height: 6 }, { x: 6, y: 6, width: 3, height: 6 }, { x: 9, y: 0, width: 3, height: 6 }, { x: 9, y: 6, width: 3, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            8: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 3, height: 6 }, { x: 3, y: 0, width: 3, height: 6 }, { x: 6, y: 0, width: 3, height: 6 }, { x: 9, y: 0, width: 3, height: 6 }, { x: 0, y: 6, width: 3, height: 6 }, { x: 3, y: 6, width: 3, height: 6 }, { x: 6, y: 6, width: 3, height: 6 }, { x: 9, y: 6, width: 3, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            9: [
                { gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 4, height: 4 }, { x: 4, y: 0, width: 4, height: 4 }, { x: 8, y: 0, width: 4, height: 4 }, { x: 0, y: 4, width: 4, height: 4 }, { x: 4, y: 4, width: 4, height: 4 }, { x: 8, y: 4, width: 4, height: 4 }, { x: 0, y: 8, width: 4, height: 4 }, { x: 4, y: 8, width: 4, height: 4 }, { x: 8, y: 8, width: 4, height: 4 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ],
            10: [
                { gridLayout: { cols: 15, rows: 12, cells: [{ x: 0, y: 0, width: 3, height: 6 }, { x: 3, y: 0, width: 3, height: 6 }, { x: 6, y: 0, width: 3, height: 6 }, { x: 9, y: 0, width: 3, height: 6 }, { x: 12, y: 0, width: 3, height: 6 }, { x: 0, y: 6, width: 3, height: 6 }, { x: 3, y: 6, width: 3, height: 6 }, { x: 6, y: 6, width: 3, height: 6 }, { x: 9, y: 6, width: 3, height: 6 }, { x: 12, y: 6, width: 3, height: 6 }] }, fullBleed: false, showGutter: true, layoutEngineType: "flex" }
            ]
        };

        // Use 6-cell layout for 7, use 4-cell for missing counts, fallback to 1-cell for edge cases
        return layouts[count] || layouts[6] || layouts[4] || layouts[1];
    }
}
